import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { ChevronDown, Lock, LockOpen, Trash2 } from "lucide-react";
import { Suspense, useState } from "react";
import { capitalize } from "shared";
import { toast } from "sonner";
import { GivefrontCardIcon } from "@/components/icons/credit-card-icon";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRequiredRole } from "@/hooks/useRequiredRole";

export const Route = createFileRoute("/_authed/_org/cards/$id")({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc }, params: { id } }) => {
    queryClient.ensureQueryData(
      trpc.card.get.queryOptions({
        cardId: id,
      }),
    );
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const [cancelOpen, setCancelOpen] = useState(false);
  const { id } = Route.useParams();

  const { queryClient, trpc } = Route.useRouteContext();
  const location = useLocation();
  const { admin } = useRequiredRole();

  const handleInvalidations = () => {
    queryClient.invalidateQueries(
      trpc.card.get.queryOptions({
        cardId: id,
      }),
    );
    queryClient.invalidateQueries(trpc.card.list.queryOptions());
  };

  const { data: card } = useSuspenseQuery(
    trpc.card.get.queryOptions({
      cardId: id,
    }),
  );

  const { mutate: freeze } = useMutation(
    trpc.card.freeze.mutationOptions({
      onSuccess: () => {
        // handleInvalidations();
        toast.info("Card Locked", {
          position: "bottom-left",
        });
      },
    }),
  );
  const { mutate: unfreeze } = useMutation(
    trpc.card.unfreeze.mutationOptions({
      onSuccess: () => {
        // handleInvalidations();
        toast.success("Card Unlocked", {
          position: "bottom-left",
        });
      },
    }),
  );
  const { mutate: cancel } = useMutation(
    trpc.card.cancel.mutationOptions({
      onSuccess: () => {
        handleInvalidations();
        toast.info("Card canceled", {
          position: "bottom-left",
        });
      },
    }),
  );

  const canUnfreeze = card.statusReason === null || admin;

  return (
    <>
      <SheetHeader className="border-b">
        <div className="flex flex-row items-start">
          <GivefrontCardIcon
            variant={card.type}
            className="-mt-0.5 mr-2 size-10"
          />

          <div className="flex flex-col">
            <SheetTitle>{card.name}</SheetTitle>
            <div className="flex flex-row justify-between">
              <span className="text-sm">
                {capitalize(card.type)} Card ...{card.last4}
              </span>
            </div>
          </div>

          <div className="mt-1 mr-8 ml-auto pl-2">
            {card.status !== "canceled" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="mr-2">
                  <Button size="sm" variant="outline">
                    Actions
                    <ChevronDown
                      aria-hidden="true"
                      className="-me-1 ms-2 opacity-60"
                      size={16}
                      strokeWidth={2}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {card.status === "active" && (
                    <DropdownMenuItem
                      onClick={() =>
                        freeze({
                          cardId: id,
                        })
                      }
                    >
                      <Lock
                        aria-hidden="true"
                        className="opacity-60"
                        size={16}
                        strokeWidth={2}
                      />
                      Lock Card
                    </DropdownMenuItem>
                  )}
                  {card.status === "frozen" && (
                    <DropdownMenuItem
                      disabled={!canUnfreeze}
                      onClick={() =>
                        unfreeze({
                          cardId: id,
                        })
                      }
                    >
                      <LockOpen
                        aria-hidden="true"
                        className="opacity-60"
                        size={16}
                        strokeWidth={2}
                      />
                      Unlock Card
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setCancelOpen(true)}>
                    <Trash2
                      aria-hidden="true"
                      className="opacity-60"
                      size={16}
                      strokeWidth={2}
                    />
                    Cancel Card
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <AlertDialog onOpenChange={setCancelOpen} open={cancelOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel {card.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This action can not be undone. Only cancel a physical card if
                you have lost it or the cardholder is no longer a part of your
                organization.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>No Thanks</AlertDialogCancel>

              <Button
                onClick={() => {
                  cancel({
                    cardId: id,
                  });
                  setCancelOpen(false);
                }}
                variant={"destructive"}
              >
                <Trash2 />
                Cancel {card.name}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Tabs
          defaultValue="overview"
          onValueChange={(value) =>
            navigate({
              to: value,
            })
          }
          value={location.href}
        >
          <TabsList className="h-auto gap-2 rounded-none bg-transparent px-0 text-foreground">
            <TabsTrigger value={`/cards/${id}`}>Overview</TabsTrigger>
            <TabsTrigger value={`/cards/${id}/transactions`}>
              Transactions
            </TabsTrigger>
            <TabsTrigger value={`/cards/${id}/activity`}>Activity</TabsTrigger>

            {card.status !== "canceled" && admin && (
              <TabsTrigger value={`/cards/${id}/edit`}>Edit</TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </SheetHeader>
      <Suspense
        fallback={
          <div className="px-6">
            <SheetSkeleton />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </>
  );
}
